import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Bookmark, Gauge, Maximize, Minimize, Pause, Play, RotateCcw, ShieldAlert, SkipBack, SkipForward } from 'lucide-react';
import { LoadingState } from '../components/ui';
import { pointerId, queryClass, type ParseRecord } from '../lib/parse';
import { loadWatchedLectures, saveBookmarkRow, type BookmarkRow } from '../lib/studentUtilities';
import { loadVideoProgress, saveVideoProgress } from '../lib/videoProgress';
import { enterPictureInPicture, onAppStateChanged, onCaptureDetected, setBackgroundKeepAlive, runningNatively, setCaptureProtection } from '../lib/nativeProtection';
import { getOfflineLecture, offlineLectureUrl } from '../lib/offlineDownloads';
import { OfflineDownloadButton } from '../components/OfflineDownloadButton';

// Exact native speed list (VideoPlayer.js speedRatio) — pitch preserved at every step.
const SPEEDS = [0.7, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0, 2.2];
const QUICK_SPEEDS = [0.75, 1.0, 1.25, 1.5, 2.0];
const SAVE_INTERVAL_MS = 5000;
const RESUME_THRESHOLD = 5;
const SYLLABUS_RETURN_KEY = 'ayurguide-course-syllabus-return';

function readSyllabusReturn() {
  try {
    const value = JSON.parse(sessionStorage.getItem(SYLLABUS_RETURN_KEY) || 'null') as { returnTo?: string; returnScrollY?: number } | null;
    return value && typeof value.returnTo === 'string' ? value : undefined;
  } catch { return undefined; }
}

function formatTime(totalSeconds: number) {
  const seconds = Math.max(0, Math.floor(totalSeconds || 0));
  const minutes = Math.floor(seconds / 60);
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

export function StudentVideoPlayer() {
  const { videoId } = useParams<{ videoId: string }>();
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const latestRef = useRef({ time: 0, duration: 0 });
  const resumedRef = useRef(false);
  const appActiveRef = useRef(true);
  const playIntentRef = useRef(false);
  const [video, setVideo] = useState<ParseRecord>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1.0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [resumedFrom, setResumedFrom] = useState<number>();
  const [captureBlocked, setCaptureBlocked] = useState(false);
  const [bookmarkRow, setBookmarkRow] = useState<BookmarkRow>();
  const [bookmarked, setBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(true);
  const [bookmarkSaving, setBookmarkSaving] = useState(false);
  const [offlineVideoUrl, setOfflineVideoUrl] = useState<string>();
  const courseId = pointerId(video?.CoursePtr);
  const playerReturnState = location.state as { returnTo?: unknown; returnScrollY?: unknown } | null;
  const storedReturn = readSyllabusReturn();
  const candidateReturnTo = typeof playerReturnState?.returnTo === 'string' ? playerReturnState.returnTo : storedReturn?.returnTo || '';
  const returnPath = `/courses/${courseId}?tab=syllabus`;
  const [candidatePath, candidateSearch = ''] = candidateReturnTo.split('?');
  const candidateParams = new URLSearchParams(candidateSearch);
  const validReturnTo = Boolean(courseId && candidatePath === `/courses/${courseId}` && candidateParams.get('tab') === 'syllabus' && [...candidateParams.keys()].every((key) => ['tab', 'subject', 'folder', 'loaded'].includes(key)));
  const returnTo = validReturnTo ? `${candidatePath}?${candidateParams.toString()}` : returnPath;
  const returnScrollY = typeof playerReturnState?.returnScrollY === 'number'
    ? Math.max(0, playerReturnState.returnScrollY)
    : storedReturn?.returnTo === returnTo && typeof storedReturn.returnScrollY === 'number' ? Math.max(0, storedReturn.returnScrollY) : undefined;

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const rows = await queryClass('elearning_Videos', {
          limit: 1,
          keys: ['VideoName', 'VideoDesc', 'OriginalFileURL', 'VideoLogo', 'VideoDuration', 'CoursePtr'],
          where: (query) => query.equalTo('objectId', String(videoId)),
        });
        if (!active) return;
        if (!rows[0]?.OriginalFileURL) { setError('This lecture has no playable video yet.'); return; }
        setVideo(rows[0]);
        const offline = await getOfflineLecture(String(videoId)).catch(() => undefined);
        if (offline && active) setOfflineVideoUrl(offlineLectureUrl(offline));
        const saved = await loadVideoProgress(String(videoId)).catch(() => undefined);
        if (active && saved && saved.time > RESUME_THRESHOLD) {
          resumedRef.current = true;
          latestRef.current.time = saved.time;
          setResumedFrom(saved.time);
          setCurrent(saved.time);
        }
      } catch {
        if (active) setError('We could not load this lecture.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [videoId]);

  const persist = useCallback(() => {
    const { time, duration: total } = latestRef.current;
    if (!videoId || !total || time < 1) return;
    void saveVideoProgress(videoId, Math.floor(time), Math.floor(total));
  }, [videoId]);

  // Load bookmark state for this lecture.
  useEffect(() => {
    let active = true;
    setBookmarkLoading(true);
    setBookmarkRow(undefined);
    setBookmarked(false);
    void loadWatchedLectures().then((row) => {
      if (!active) return;
      setBookmarkRow(row);
      setBookmarked(Boolean(row.Bookmarks?.some((entry) => entry.id === videoId)));
    }).catch(() => undefined).finally(() => { if (active) setBookmarkLoading(false); });
    return () => { active = false; };
  }, [videoId]);

  const toggleBookmark = useCallback(async () => {
    if (!videoId || bookmarkLoading || bookmarkSaving) return;
    const current = { id: videoId, time: Math.floor(latestRef.current.time), totalTime: Math.floor(latestRef.current.duration) };
    const previousEntries = bookmarkRow?.Bookmarks ?? [];
    const nextEntries = bookmarked
      ? previousEntries.filter((entry) => entry.id !== videoId)
      : [...previousEntries.filter((entry) => entry.id !== videoId), current];
    setBookmarked(!bookmarked);
    setBookmarkSaving(true);
    try {
      const savedRow = await saveBookmarkRow(bookmarkRow ?? ({ objectId: '', Bookmarks: [] } as BookmarkRow), nextEntries);
      setBookmarkRow(savedRow);
    } catch { setBookmarked(bookmarked); }
    finally { setBookmarkSaving(false); }
  }, [videoId, bookmarkRow, bookmarked, bookmarkLoading, bookmarkSaving]);

  // Periodic save while playing + terminal saves.
  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(persist, SAVE_INTERVAL_MS);
    const flush = () => persist();
    document.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', flush);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [playing, persist]);

  useEffect(() => {
    const resumeIfNeeded = () => {
      if (playIntentRef.current && videoRef.current?.paused) void videoRef.current.play().catch(() => undefined);
    };
    const handleVisibilityChange = () => {
      appActiveRef.current = !document.hidden;
      resumeIfNeeded();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const remove = onAppStateChanged((isActive) => {
      appActiveRef.current = isActive;
      resumeIfNeeded();
    });
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void remove.then((off) => off?.());
    };
  }, []);

  // Capture protection is always on during playback (hardcoded product rule).
  useEffect(() => {
    setCaptureProtection(playing);
    void setBackgroundKeepAlive(playing);
    if (!playing) return () => undefined;
    const remove = onCaptureDetected(() => {
      videoRef.current?.pause();
      setCaptureBlocked(true);
    });
    return () => { setCaptureProtection(false); void setBackgroundKeepAlive(false); void remove.then((off) => off?.()); };
  }, [playing]);

  useEffect(() => () => { void setCaptureProtection(false); }, []);

  const applySpeed = (rate: number) => {
    const el = videoRef.current;
    if (el) { el.playbackRate = rate; el.preservesPitch = true; }
    setSpeed(rate);
    setMenuOpen(false);
  };

  const jumpBy = (seconds: number) => {
    const el = videoRef.current;
    if (!el) return;
    const total = el.duration || Number(video?.VideoDuration) || 0;
    const next = Math.max(0, Math.min(total || Infinity, el.currentTime + seconds));
    el.currentTime = next;
    latestRef.current.time = next;
    setCurrent(next);
    persist();
  };

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) void el.play(); else el.pause();
  };

  const toggleFullscreen = async () => {
    const container = videoRef.current?.parentElement;
    if (!container) return;
    try {
      if (document.fullscreenElement) { await document.exitFullscreen(); setFullscreen(false); }
      else { await container.requestFullscreen(); setFullscreen(true); }
    } catch { /* denied */ }
  };

  const onLoadedMetadata = () => {
    const el = videoRef.current;
    if (!el) return;
    el.preservesPitch = true;
    setDuration(el.duration || 0);
    latestRef.current.duration = el.duration || 0;
    if (resumedRef.current && resumedFrom && resumedFrom < el.duration - RESUME_THRESHOLD) el.currentTime = resumedFrom;
  };

  if (loading) return <div className="player-page"><LoadingState /></div>;
  if (error) return <div className="player-page"><div className="player-error"><ShieldAlert size={26} /><p>{error}</p><Link className="back-link" to="/courses">Back to courses</Link></div></div>;

  return (
    <div className={`player-page${fullscreen ? ' fullscreen' : ''}`}>
      <header className="player-top">
        {!fullscreen && <Link to={courseId ? returnTo : '/courses'} state={returnScrollY === undefined ? null : { restoreScrollY: returnScrollY }} className="back-link" aria-label={courseId ? 'Back to course syllabus' : 'Back to courses'}><ArrowLeft size={18} /> {courseId ? 'Course syllabus' : 'Courses'}</Link>}
        <strong>{video?.VideoName}</strong>
        <div className="player-top-actions">
        {video?.OriginalFileURL && <OfflineDownloadButton id={String(videoId)} title={String(video.VideoName ?? 'Lecture')} description={video.VideoDesc} sourceUrl={String(video.OriginalFileURL)} duration={Number(video.VideoDuration) || undefined} thumbnail={String(video.VideoLogo ?? '')} compact />}
          <button className={`text-button bookmark-toggle${bookmarked ? ' active' : ''}`} disabled={bookmarkLoading || bookmarkSaving} onClick={() => void toggleBookmark()}>
            <Bookmark size={14} /> {bookmarked ? 'Saved' : 'Save'}
          </button>
          {runningNatively() && <button className="text-button" onClick={() => enterPictureInPicture()}>PiP</button>}
        </div>
      </header>
      <div className="player-stage">
        <video
          ref={videoRef}
          src={offlineVideoUrl ?? video?.OriginalFileURL}
          poster={video?.VideoLogo}
          playsInline
          preload="metadata"
          onClick={togglePlay}
          onDoubleClick={toggleFullscreen}
          onLoadedMetadata={onLoadedMetadata}
          onPlay={() => { playIntentRef.current = true; setPlaying(true); }}
          onPause={() => {
            persist();
            if (appActiveRef.current) {
              playIntentRef.current = false;
              setPlaying(false);
            }
          }}
          onEnded={() => { playIntentRef.current = false; setPlaying(false); persist(); }}
          onTimeUpdate={(event) => {
            const el = event.currentTarget;
            latestRef.current.time = el.currentTime;
            setCurrent(el.currentTime);
          }}
        />
        {captureBlocked && (
          <div className="capture-block">
            <ShieldAlert size={30} />
            <strong>Screen recording detected</strong>
            <span>Playback paused to protect this content.</span>
            <button onClick={() => { setCaptureBlocked(false); void videoRef.current?.play(); }} disabled>Close recording app to continue</button>
          </div>
        )}
        {resumedFrom !== undefined && !playing && !captureBlocked && (
          <button className="resume-toast" onClick={() => setResumedFrom(undefined)}>
            <RotateCcw size={14} /> Resumed from {formatTime(resumedFrom)}
          </button>
        )}
        {!playing && !captureBlocked && (
          <button className="player-big-play" onClick={togglePlay} aria-label="Play"><Play size={30} /></button>
        )}
        <div className="player-bar player-bar-enhanced">
          <div className="player-progress-row">
            <input
              type="range"
              min={0}
              max={duration || Number(video?.VideoDuration) || 0}
              step={1}
              value={Math.min(current, duration || Infinity) || 0}
              onChange={(event) => {
                const next = Number(event.target.value);
                setCurrent(next);
                latestRef.current.time = next;
                if (videoRef.current) videoRef.current.currentTime = next;
              }}
              aria-label="Seek"
            />
            <span className="player-time">{formatTime(current)} / {formatTime(duration)}</span>
          </div>
          <div className="player-control-row">
            <button type="button" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <button type="button" className="seek-skip-button" onClick={() => jumpBy(-5)} aria-label="Rewind 5 seconds"><SkipBack size={17} /><small>5</small></button>
            <button type="button" className="seek-skip-button" onClick={() => jumpBy(15)} aria-label="Forward 15 seconds"><SkipForward size={17} /><small>15</small></button>
            <div className="speed-anchor">
              <button type="button" className={speed !== 1 ? 'speed-active' : ''} onClick={() => setMenuOpen((open) => !open)} aria-label={`Playback speed ${speed} times`} aria-expanded={menuOpen} aria-haspopup="menu">
                <Gauge size={18} /> {speed}×
              </button>
              {menuOpen && (
                <div className="speed-menu speed-menu-expanded" role="menu" aria-label="Playback speed options">
                  <div className="speed-menu-group" role="group" aria-label="Common speeds">
                    <strong>Common speeds</strong>
                    <div className="speed-presets">{QUICK_SPEEDS.map((rate) => <button key={rate} type="button" role="menuitemradio" aria-checked={rate === speed} className={rate === speed ? 'active' : ''} onClick={() => applySpeed(rate)}>{rate === 1 ? '1×' : `${rate}×`}</button>)}</div>
                  </div>
                  <div className="speed-menu-group speed-menu-fine" role="group" aria-label="Fine control">
                    <strong>Fine control</strong>
                    {SPEEDS.map((rate) => (
                      <button key={rate} type="button" role="menuitemradio" aria-checked={rate === speed} className={rate === speed ? 'active' : ''} onClick={() => applySpeed(rate)}>
                        {rate === 1 ? 'Normal' : `${rate}×`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button type="button" onClick={toggleFullscreen} aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              {fullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          </div>
        </div>
      </div>
      <section className="player-meta">
        <h1>{video?.VideoName}</h1>
        <p>{video?.VideoDesc}</p>
      </section>
    </div>
  );
}
